// Curated customer reviews shown inside the /boka flow.
//
// WHY hardcoded (not live Trustpilot): the Trustpilot TrustBox widget is a
// locked iframe with no styling control, and the read-reviews Business API is
// gated behind a paid plan + key. Curating our best reviews here gives full
// design control AND lets us hand-pick testimonials that defuse specific
// objections at the booking step. If we later get a Trustpilot API key, swap
// the source of `BOOKING_REVIEWS` for a cached server fetch — the component
// (`BookingReviews.tsx`) reads this shape and won't need to change.
//
// The reviews below are REAL entries pulled from
// https://se.trustpilot.com/review/ungdomskraft.com (curated, 5★).
// ⚠️ Still confirm REVIEW_AGGREGATE (score + count) against the live TrustScore.

export type ReviewServiceTag =
  | "bortforsling"
  | "transport"
  | "montering"
  | "bud"
  | "barhjälp"
  | "annat"
  | "allmänt";

export interface BookingReview {
  /** Display name as shown on Trustpilot (first name + last initial is fine). */
  author: string;
  /** 1–5 stars. Only feature 4–5 in the flow. */
  rating: number;
  /** "YYYY-MM" — rendered as a localized month + year. */
  date: string;
  /** Original Swedish text + an English rendering for /en/book. */
  text: { sv: string; en: string };
  /** Which service this praises — drives the little tag chip. */
  service?: ReviewServiceTag;
}

/** Aggregate badge shown above the rotating card. Confirm against live Trustpilot. */
export const REVIEW_AGGREGATE = {
  score: 4.9,
  count: 600,
  url: "https://se.trustpilot.com/review/ungdomskraft.com",
};

const serviceLabel: Record<ReviewServiceTag, { sv: string; en: string }> = {
  bortforsling: { sv: "Bortforsling", en: "Junk removal" },
  transport: { sv: "Transport", en: "Transport" },
  montering: { sv: "Möbelmontering", en: "Furniture assembly" },
  bud: { sv: "Budbil", en: "Courier" },
  barhjälp: { sv: "Bärhjälp", en: "Carrying help" },
  annat: { sv: "Övrigt", en: "Other" },
  allmänt: { sv: "Omdöme", en: "Review" },
};

export function reviewServiceLabel(tag: ReviewServiceTag | undefined, locale: "sv" | "en"): string {
  return serviceLabel[tag ?? "allmänt"][locale];
}

export const BOOKING_REVIEWS: BookingReview[] = [
  {
    author: "Mia Tottmar",
    rating: 5,
    date: "2026-06",
    service: "allmänt",
    text: {
      sv: "Mycket nöjd. Glada grabbar, snabbt, effektivt och helt sömlöst. Önskar att mina egna kids funkade så.",
      en: "Very happy. Cheerful guys, fast, efficient and completely seamless. Wish my own kids worked like that.",
    },
  },
  {
    author: "Soula",
    rating: 5,
    date: "2026-06",
    service: "allmänt",
    text: {
      sv: "Snabba, duktiga och professionella. Arbetet utfördes med hög kvalitet och gott bemötande. Rekommenderas varmt!",
      en: "Fast, skilled and professional. The work was done to a high standard with a great attitude. Warmly recommended!",
    },
  },
  {
    author: "Ragnhild Blomdahl",
    rating: 5,
    date: "2026-06",
    service: "barhjälp",
    text: {
      sv: "Jättenöjd! Trevliga, proffsiga och lösningsinriktade (fixade t ex ett gammalt stort skrivbord ner för en trång trappa)! Bara lovord.",
      en: "So happy! Friendly, professional and solution-oriented (e.g. got a big old desk down a narrow stairwell)! Nothing but praise.",
    },
  },
  {
    author: "Eva T",
    rating: 5,
    date: "2026-06",
    service: "transport",
    text: {
      sv: "Snabb kontakt efter förfrågan om hjälp. Kom vid utsatt tid. Supertrevligt bemötande och snabbt och proffsigt genomförande av möbelförflyttningen. Kan varmt rekommenderas!",
      en: "Quick response after I asked for help. Arrived on time. Super friendly, with fast and professional handling of the furniture move. Warmly recommended!",
    },
  },
  {
    author: "Peter",
    rating: 5,
    date: "2026-06",
    service: "barhjälp",
    text: {
      sv: "Jag är väldigt nöjd med den bärhjälp jag fick. Flinka, starka och sympatiska killar. Jag kommer att anlita dem igen.",
      en: "I'm very happy with the carrying help I got. Nimble, strong and likeable guys. I'll hire them again.",
    },
  },
  {
    author: "Liisa",
    rating: 5,
    date: "2026-05",
    service: "bortforsling",
    text: {
      sv: "Kom tidigt på eftermiddagspasset och var snabba, starka och proffsiga när de tömde ett källarförråd. Bra kommunikation från kundservicen innan besöket också.",
      en: "Arrived early in the afternoon slot and were fast, strong and professional clearing out a basement storage. Good communication from customer service before the visit too.",
    },
  },
  {
    author: "Camilla Bohman",
    rating: 5,
    date: "2026-05",
    service: "allmänt",
    text: {
      sv: "Alltid snabba och trevliga. Inget är något problem, de fixar allt!",
      en: "Always fast and friendly. Nothing is a problem, they sort everything out!",
    },
  },
  {
    author: "Ewa Grettve",
    rating: 5,
    date: "2026-05",
    service: "bud",
    text: {
      sv: "Service! Bokade och ca 2 timmar senare var min vara här och inburen. Rekommenderar dem.",
      en: "Service! I booked and about 2 hours later my item was here and carried in. I recommend them.",
    },
  },
  {
    author: "Susanne Falk",
    rating: 5,
    date: "2026-05",
    service: "barhjälp",
    text: {
      sv: "Smidigt och snabbt att beställa. Supertrevliga killar som var hjälpsamma. Proffsigt! Vill gärna anlita dem igen.",
      en: "Smooth and quick to book. Super friendly guys who were helpful. Professional! Would gladly hire them again.",
    },
  },
  {
    author: "HRF Stockholms län",
    rating: 5,
    date: "2026-05",
    service: "bortforsling",
    text: {
      sv: "Vi har använt Ungdomskraft tidigare och valde att anlita dem igen. Herrarna som hämtar är mycket hjälpsamma, trevliga och professionella. Bra att de kommunicerade via sms innan de skulle anlända så man var beredd.",
      en: "We've used Ungdomskraft before and chose to hire them again. The men who do the pickup are very helpful, friendly and professional. Nice that they texted before arriving so we were ready.",
    },
  },
];
